import { useEffect, useRef, type ReactNode } from 'react';

import { cn } from '~/utils/cn';

import { Icon } from '../../atoms/Icon/view';
import { Text } from '../../atoms/Text/view';
import type { propsIconsNames } from '../../atoms/Icon/interfaces';

export interface ContractActionItem {
  key: string;
  label: string;
  description?: string;
  icon: propsIconsNames;
  destructive?: boolean;
  locked?: boolean;
  onClick: () => void;
}

interface PropsContractActionsMenu {
  open: boolean;
  onClose: () => void;
  actions: ContractActionItem[];
  children: ReactNode;
}

export default function ContractActionsMenu({
  open,
  onClose,
  actions,
  children,
}: PropsContractActionsMenu) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) onClose();
    };
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open, onClose]);

  return (
    <div ref={containerRef} className="relative">
      {children}

      {open ? (
        <div
          role="menu"
          className="absolute right-0 top-[calc(100%+8px)] z-50 w-[300px] overflow-hidden rounded-medium border border-intense-03 bg-intense-01 shadow-level3 animate-fade_in"
        >
          {actions.map((action, index) => (
            <button
              key={action.key}
              type="button"
              role="menuitem"
              onClick={action.onClick}
              className={cn(
                'flex w-full items-start gap-smaller px-small py-smaller text-left hover:bg-intense-02',
                index > 0 && 'border-t border-intense-03',
              )}
            >
              <span
                className={cn(
                  'mt-[2px] flex h-[32px] w-[32px] shrink-0 items-center justify-center rounded-[10px]',
                  action.destructive ? 'bg-warning-01' : 'bg-support-01',
                )}
              >
                <Icon
                  name={action.icon}
                  size={16}
                  color={action.destructive ? '#B3261E' : '#1D4733'}
                  rounded={false}
                />
              </span>
              <span className="flex-1">
                <span className="flex items-center gap-quark">
                  <Text
                    className={cn(
                      'text-paragraphMD',
                      action.destructive
                        ? 'color-warning-06'
                        : 'color-complement-01',
                    )}
                  >
                    {action.label}
                  </Text>
                  {action.locked ? (
                    <Icon
                      name="icon-lock"
                      size={12}
                      color="#606060"
                      rounded={false}
                    />
                  ) : null}
                </span>
                {action.description ? (
                  <Text className="mt-quark block text-captionMD color-intense-06">
                    {action.description}
                  </Text>
                ) : null}
              </span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
